import { useCallback, useMemo } from 'react';
import { enqueueSnackbar } from 'notistack';
import { captureException } from 'scalarAnalytics/postHog';
import { allowedConcurrentRequests, pollingConfig } from './constants';
import { RolloverParams, UseRolloverParams } from './rollover.types';
import { ApiService, MeasurementDateRollover } from '../../../api';
import { separateIntoMultipleRequests } from '../../../components/MeasurementDates/utilities/separate-into-multiple-requests/separateIntoMultipleRequests';
import { MultipleRequestTypes } from '../../../components/MeasurementDates/utilities/separate-into-multiple-requests/separateIntoMultipleRequests.types';
import useRollingApiRequest from '../../../hooks/useRollingApiRequest/useRollingApiRequest';
import { COMPLETE, INITIAL } from '../../../hooks/useRollingApiRequest/useRollingApiRequest.props';
import { dbShortDate, extractServerErrorMessage, typedObjectValues } from '../../../utilities';

// Reports every failed rollover and returns the server's reason when the body carries one.
const getRolloverErrorMessage = (error: unknown) => {
  captureException(error, { source: 'measurement_date_rollover' });
  return extractServerErrorMessage((error as { response?: { data?: unknown } })?.response?.data);
};

// The measurement date exists even when some valuation results could not be priced.
const createRollover = async (data: MeasurementDateRollover) => {
  const response = await ApiService.apiMeasurementDatesCompanyRolloverCreate(data);
  const warnings = response?.unpriced_companies ?? [];
  if (warnings.length) {
    enqueueSnackbar(
      `Measurement date created with unpriced valuations. ${warnings.map(({ company, reason }) => `${company}: ${reason}`).join(' ')}`,
      { variant: 'warning', persist: true }
    );
  }
  return response;
};

const useRolloverCompanies = ({
  firmInfo,
  measurementDate,
  selectedFunds,
  rolloverDetailCompanies,
}: UseRolloverParams) => {
  // 1: create the rollover request data
  const { fundRequests, companyRequests } = useMemo<MultipleRequestTypes>(() => {
    if (!measurementDate || !firmInfo?.id) {
      return {
        fundRequests: [],
        companyRequests: [],
      };
    }

    const postData: RolloverParams = {
      newMeasurementDate: dbShortDate(measurementDate) as string,
      funds: selectedFunds,
      firmId: firmInfo?.id,
      rollovers: [],
      companies: [],
    };

    postData.rollovers = rolloverDetailCompanies;

    return separateIntoMultipleRequests(postData);
  }, [firmInfo, selectedFunds, measurementDate, rolloverDetailCompanies]);

  const rolloverCreate = useCallback(async (data: MeasurementDateRollover) => {
    const response = await createRollover(data);
    return response?.company_mds?.[0];
  }, []);

  const companyPollMethod = useCallback(async (request: MeasurementDateRollover) => {
    if (request.company_rollovers?.length !== 1) {
      return undefined;
    }
    const companyId = request.company_rollovers[0].company_id.toString();
    const response = await ApiService.apiCompanyMeasurementDatesExistsRead(companyId, request.new_measurement_date);
    return response?.company_measurement_date;
  }, []);

  const getFundRequestId = useCallback((rollover: MeasurementDateRollover) => {
    if (rollover.fund_ids?.length === 1) {
      return rollover.fund_ids[0];
    }
    throw new Error('Fund Id not found');
  }, []);

  const getRolloverRequestId = useCallback((rollover: MeasurementDateRollover) => {
    if (rollover.company_rollovers?.length === 1) {
      return rollover.company_rollovers[0].company_id;
    }
    throw new Error('Company Id not found');
  }, []);

  const {
    doRequest: doFundRequest,
    result: fundResult,
    processStatus: fundStatus,
  } = useRollingApiRequest({
    promisesParams: fundRequests,
    promiseHandler: createRollover,
    getRequestId: getFundRequestId,
    getErrorMessage: getRolloverErrorMessage,
    concurrentRequests: fundRequests.length,
  });

  const {
    doRequest: doCompanyRequest,
    result: companyResult,
    processStatus: companyStatus,
  } = useRollingApiRequest({
    promisesParams: companyRequests,
    pollingConfig: {
      ...pollingConfig,
      pollMethod: companyPollMethod,
    },
    promiseHandler: rolloverCreate,
    concurrentRequests: allowedConcurrentRequests,
    getRequestId: getRolloverRequestId,
    getErrorMessage: getRolloverErrorMessage,
  });

  const processingRollover = useMemo<boolean>(
    () =>
      //   if fundStatus, companyStatus are all not in the status of either 'initial' or 'complete', then we are still processing the rollover
      ![fundStatus, companyStatus].includes(INITIAL) && ![fundStatus, companyStatus].includes(COMPLETE),
    [fundStatus, companyStatus]
  );

  const result = useMemo(
    () => ({
      fundResult: typedObjectValues(fundResult),
      companyResult: typedObjectValues(companyResult),
    }),
    [companyResult, fundResult]
  );

  const launchRequests = useCallback(() => {
    doFundRequest();
    doCompanyRequest();
  }, [doCompanyRequest, doFundRequest]);

  return {
    launchRequests,
    processingRollover,
    result,
  };
};

export default useRolloverCompanies;
